<template>
    <div>
        <div v-if="header.value === 'period'" class="caption">{{ periodText }}</div>
        <div v-else-if="header.value === 'status'" class="d-flex justify-end">
            <v-icon v-if="item.mon_status === 1" color="secondary"> mdi-alert-circle</v-icon>
            <v-icon v-else-if="item.mon_status === 3" color="error"> mdi-close-circle</v-icon>
            <v-icon v-else-if="item.mon_status === 2" color="#43CA01"> mdi-check-circle</v-icon>
        </div>
        <div v-else-if="['total_time_plus', 'extra_time_plus'].includes(header.value)" class="caption d-flex pr-5 align-center justify-end">
            <v-tooltip bottom>
                <template #activator="{ on }">
                    <span v-on="on" :class="colorText + '--text'" class="text-right" style="white-space: nowrap"> {{ timeText }} </span>
                </template>
                {{ $t("Actual / monitoring (month)") }}
            </v-tooltip>
        </div>
        <div
            v-else-if="!isNaN(percentageText) && percentageText !== null"
            class="d-flex align-center justify-end pr-5" :class="colorText + '--text'">
            <v-tooltip bottom>
                <template #activator="{ on }">
                    <div v-on="on" class="caption" style="white-space: nowrap">
                        <span> {{ percentageText }}% </span>
                        <v-icon v-if="!isNaN(item[headerKey + '_percent'])" small :color="colorText"> {{ arrowIcon }}</v-icon>
                        <span>{{ differenceText }}</span>
                    </div>
                </template>
                {{ tooltipText }}
            </v-tooltip>
        </div>
    </div>
</template>

<script>
import {$t} from "../../i18n";
import {format_time_from_seconds} from "../../helpers/formatting";
import {computed} from "@vue/composition-api";
import {CustomFormats, formatDateCustom, formatNumber, parseSysDate} from "../../plugins/formatting";

export default {
    name: "MonitoringColumn",
    props: {
        item: {
            type: Object,
            required: true
        },
        header: {
            type: Object,
            required: true
        },
        dateFrom: {
            type: String,
            default: null
        },
        dateUntil: {
            type: String,
            default: null
        },
        periodFrom: {
            type: String,
            default: null
        },
        periodUntil: {
            type: String,
            default: null
        },
        periodCoefficient: {
            type: Number,
            default: null
        }
    },
    setup(props) {
        const headerName = computed(() => {
            if (props.header.value.endsWith("_plus")) {
                const parts = props.header.value.split("_");
                parts.pop();
                return parts.join("_");
            }
            return props.header.value;
        });
        const headerKey = computed(() => {
            if (headerName.value.startsWith("custom_field/")) {
                return headerName.value.split("/")[1];
            } else if (headerName.value.startsWith("topic/")) {
                return headerName.value.split("/")[1];
            } else {
                return headerName.value;
            }
        });

        const monitoredField = computed(() => {
            return props.item.monitor.find(mon => mon.type === headerName.value)
        });

        const actualValue = computed(() => {
            return props.item[headerKey.value] ? props.item[headerKey.value] : 0;
        });
        const expectedValue = computed(() => {
            if (monitoredField.value) {
                if (props.header.type === "mon_number") {
                    return monitoredField.value.value * props.periodCoefficient;
                } else {
                    return monitoredField.value.value * 3600 * props.periodCoefficient;
                }
            }
            return null;
        });

        const percentageText = computed(() => {
            if (expectedValue && expectedValue.value && actualValue && !isNaN(actualValue.value)) {
                return Math.round((actualValue.value * 100) / expectedValue.value);
            }
            return null;
        });
        const differenceText = computed(() => {
            let difference = actualValue.value - expectedValue.value
            if (props.header.type !== "mon_number") {
                difference = difference / 3600 // time
            }
            difference = formatNumber(Math.abs(Math.round(difference * 10) / 10), 0, 1)
            return difference + (props.header.type === "mon_number" ? '' : " " + $t("h"))
        });
        const timeText = computed(() => {
            if (monitoredField && monitoredField.value) {
                let actualText
                if (props.header.type === "mon_number") {
                    actualText = formatNumber(actualValue.value);
                } else {
                    actualText = format_time_from_seconds(actualValue.value, props.header.type === "mon_time", false) || "0:00"
                }

                let expectedText
                if (props.header.type === "mon_number") {
                    expectedText = monitoredField.value.value;
                } else {
                    expectedText = format_time_from_seconds(expectedValue.value, props.header.type === "mon_time", false)
                }
                return actualText + " / " + expectedText;
            }
        });
        const tooltipText = computed(() => {
            if (timeText.value) {
                return $t("Actual") + " " + timeText.value + " " + $t("Month").toLowerCase();
            }
            return null;
        });
        const arrowIcon = computed(() => {
            if (actualValue.value > expectedValue.value) {
                return "mdi-arrow-up-bold";
            }
            return "mdi-arrow-down-bold";
        });
        const colorText = computed(() => {
            let range = 0
            if (monitoredField.value) {
                if (props.header.type === "mon_number") {
                    range = monitoredField.value.range * props.periodCoefficient;
                } else {
                    range = monitoredField.value.range * 3600 * props.periodCoefficient;
                }
            }
            if (actualValue.value > expectedValue.value + range) {
                return "error";
            } else if (actualValue.value < expectedValue.value - range) {
                return "green";
            }
            return "black";
        });
        const periodText = computed(() => {
            if (props.periodFrom === props.periodUntil) {
                return formatDateCustom(parseSysDate(props.dateFrom), CustomFormats.numeric_month_with_short_year)
            } else {
                return (
                    formatDateCustom(parseSysDate(props.dateFrom), CustomFormats.numeric_month_with_short_year)
                    + " - " +
                    formatDateCustom(parseSysDate(props.dateUntil), CustomFormats.numeric_month_with_short_year)
                );
            }
        });

        return {
            arrowIcon,
            differenceText,
            percentageText,
            tooltipText,
            colorText,
            headerKey,
            periodText,
            timeText,
            actualValue,
            expectedValue,
            monitoredField,
        };
    }
};
</script>
